/* eslint-disable max-statements */
import { PixelRatio } from 'react-native';
import { replaceBrWithNewline, t } from 'Locale';
import Theme from 'Theme';
import { modelType, ProgramModel, v2ScheduleToV1ProgramDetail } from '@24i/st-api-connector';
import {
    Device,
    getActualWidth,
    getActualHeight,
    getDeviceName,
    getMcoreDeviceType
} from 'DeviceUtils';
import { getFirstEpisode, getRecordingForItem } from 'Utils/itemUtils';

import { toLocaleDateString, formatDay, formatMonth } from 'Utils/dateUtils';
import {
    episodesGrammarTranslationKeys,
    getGrammarCountString,
    seriesGrammarTranslationKeys
} from 'Utils/translationUtils';
import { TITLE_NUMBERING } from '@24i/st-api-connector/utils';
import { STREAM_TYPE } from '../options';

export { formatDay, formatMonth };
export const noop = () => {};
export const packshotTitleHeight = 60;

export const EMPTY_STRING_REGEXP = /^\s*$/;

export const getCurrentTime = () => Date.now();

export const addLeadingZero = (number) => `0${number}`.slice(-2);

export const getTimeStringFromMs = (milliseconds, showHours, isLive = false) => {
    let date = new Date();
    date.setHours(0, 0, 0, milliseconds);
    if (isLive) date = new Date(milliseconds);
    const minutes = date.getMinutes().toString();
    const seconds = addLeadingZero(date.getSeconds().toString());
    if (showHours) {
        return `${addLeadingZero(date.getHours().toString())}:${addLeadingZero(
            minutes
        )}:${seconds}`;
    }
    return `${minutes}:${seconds}`;
};

export const getTimeStringFromSeconds = (seconds, addZeroHours = false) => {
    let timeString = '';
    const time = new Date(0, 0, 0, 0, 0, seconds, 0);
    const hours = time.getHours();
    if (addZeroHours && hours === 0) {
        timeString = '00:';
    }
    if (hours >= 1) {
        timeString = `${addLeadingZero(hours)}:`;
    }

    timeString += `${addLeadingZero(time.getMinutes())}:${addLeadingZero(time.getSeconds())}`;

    return timeString;
};

export const getTimeStringFromTimestamp = (timestamp, addZeroHours = false) => {
    let timeString = '';
    const time = new Date(timestamp);
    const hours = time.getHours();

    if (addZeroHours && hours === 0) {
        timeString = '00:';
    }

    if (hours > 1) {
        timeString = `${addLeadingZero(hours)}:`;
    }

    timeString += `${addLeadingZero(time.getMinutes())}:${addLeadingZero(time.getSeconds())}`;

    return timeString;
};

export const formatTime = (time) => {
    const date = new Date(time);
    const hours = addLeadingZero(date.getHours());
    const minutes = addLeadingZero(date.getMinutes());
    return `${hours}:${minutes}`;
};

export const formatDayTime = (time) => {
    const date = new Date(time);
    const dayText = formatDay(date.getDay(), 2);
    const day = prependZero(date.getDate());
    const month = prependZero(date.getMonth() + 1);
    return `${dayText}, ${day}.${month}.`;
};

export const getItemStartDay = (item) => formatDayTime(item.start);
export const getItemStartTime = (item) => formatTime(item.start);
export const getItemEndTime = (item) => formatTime(item.end);

export const ITEM_TYPES = Object.freeze({
    TV: 'tv',
    TV_SERIES: 'tvSeries',
    VOD: 'vod',
    SERIES: 'series',
    SEASON: 'season',
    EPISODE: 'episode',
    RECORDING: 'recording',
    RECORDING_SERIES: 'recordingSeries',
    CONTINUE_WATCHING: 'continue-watching',
    CHANNELS: 'channels',
    MOVIE_PERSON: 'moviePerson',
    PVR: 'PVR'
});

export const isVod = (item) => item?.modelType === ITEM_TYPES.VOD;
export const isSeries = (item) => item?.modelType == ITEM_TYPES.SERIES;
export const isSeason = (item) => item?.modelType === ITEM_TYPES.SEASON;
export const isEpisode = (item) => item?.modelType === ITEM_TYPES.EPISODE;
export const isRecording = (item) => item?.modelType === ITEM_TYPES.RECORDING;
export const isRecordingSeries = (item) => item?.modelType === ITEM_TYPES.RECORDING_SERIES;
export const isLiveTv = (item) => item?.modelType === ITEM_TYPES.TV;
export const isActor = (item) => item.data?.modelType === ITEM_TYPES.MOVIE_PERSON;

export const getHorizontalPosterDimensions = () => {
    let numItems = 2;
    if (Device.isTabletOrWeb) numItems = 4;
    const gridWidth = Device.isWeb ? 1280 : getActualWidth();
    const width = PixelRatio.roundToNearestPixel((gridWidth - Theme.BU) / numItems - Theme.BU);
    return {
        width,
        height: width / Theme.sixteenByNineRatio
    };
};

export const getVerticalPosterDimensions = () => {
    let numItems = 3;
    if (Device.isTabletOrWeb) numItems = 5;
    const gridWidth = Device.isWeb ? 1280 : getActualWidth();
    const width = PixelRatio.roundToNearestPixel((gridWidth - Theme.BU) / numItems - Theme.BU);
    return {
        width,
        height: width / Theme.twentySevenByFortyOneRatio
    };
};

export const getHorizontalCarouselPosterDimensions = (forcePortrait) => {
    const displayWidth =
        getActualWidth() > getActualHeight() && forcePortrait
            ? getActualHeight()
            : getActualWidth();

    // "By eye" temporary ratio for scale
    let ratio;
    if (Device.isWeb) {
        return {
            height: 168,
            width: 292
        };
    }
    if (Device.isTablet) {
        ratio = 0.25;
    } else {
        ratio = 0.5;
    }
    const width = PixelRatio.roundToNearestPixel(displayWidth * ratio);
    return {
        width,
        height: width / Theme.sixteenByNineRatio
    };
};

export const getVerticalCarouselPosterDimensions = (forcePortrait) => {
    const displayWidth =
        getActualWidth() > getActualHeight() && forcePortrait
            ? getActualHeight()
            : getActualWidth();

    // "By eye" temporary ratio for scale
    let ratio;
    if (Device.isWeb) {
        return {
            height: 271,
            width: 190
        };
    }
    if (Device.isTablet) {
        ratio = 0.166;
    } else {
        ratio = 0.33;
    }
    const width = PixelRatio.roundToNearestPixel(displayWidth * ratio);
    return {
        width,
        height: width / Theme.twentySevenByFortyOneRatio
    };
};

export const getCarouselPackshotDimensionsByModelType = (
    modelTypeLocal = ITEM_TYPES.VOD,
    forcePortrait
) =>
    modelTypeLocal === ITEM_TYPES.VOD || modelTypeLocal === ITEM_TYPES.SERIES
        ? getVerticalCarouselPosterDimensions(forcePortrait)
        : getHorizontalCarouselPosterDimensions(forcePortrait);

export const getPackshotDimensionsByModelType = (type = ITEM_TYPES.VOD) => {
    switch (type) {
        case ITEM_TYPES.TV:
        // fall through
        case ITEM_TYPES.TV_SERIES:
        // fall through
        case ITEM_TYPES.CONTINUE_WATCHING:
        // fall through
        case ITEM_TYPES.RECORDING:
        // Fall through
        case ITEM_TYPES.RECORDING_SERIES:
            return getHorizontalPosterDimensions();
        default:
            return getVerticalPosterDimensions();
    }
};

// eslint-disable-next-line no-use-before-define, max-len
export const isArchive = (item) => isLiveTv(item) && itemHasEnded(item); // TODO: Fix these conditions, they're cyclical, should be simplified
export const isTvLikeType = (item) =>
    isLiveTv(item) || item.modelType === ITEM_TYPES.RECORDING || isArchive(item);
export const itemStartsInLessThan10Mins = (item) =>
    isTvLikeType(item) && getCurrentTime() >= item.getStartTime() - 10 * 60 * 1000;
export const itemHasStarted = (item) =>
    isTvLikeType(item) && getCurrentTime() >= item.getStartTime();
export const itemHasEnded = (item) => isTvLikeType(item) && getCurrentTime() >= item.getEndTime();
export const itemIsPlaying = (item) => !itemHasEnded(item) && itemHasStarted(item);
export const isArchiveDownloadable = (item) =>
    isArchive(item) && item.data.archiveFrom && item.data.archiveTo;
export const getMinutesBeforeStart = (item) =>
    isTvLikeType(item) && itemHasStarted(item)
        ? 0
        : Math.round((item.getStartTime() - getCurrentTime()) / 60000);

export const getTitleByModelType = (model) => {
    let title = t('COMMON.DETAILS');
    switch (model) {
        case modelType.recording:
            title = t('RECORDINGS.RECORDINGS');
            break;
        case ITEM_TYPES.VOD:
            title = t('MSEPGC_MV');
            break;
        case ITEM_TYPES.SERIES:
            title = t('COMMON.SERIES');
            break;
        case ITEM_TYPES.TV_SERIES:
            title = t('COMMON.SERIES');
            break;
        case ITEM_TYPES.TV:
            title = t('COMMON.LIVE_TV');
            break;
        case ITEM_TYPES.SEASON:
            title = t('COMMON.SEASON');
            break;
        case ITEM_TYPES.EPISODE:
            title = t('COMMON.EPISODE');
            break;
        case ITEM_TYPES.RECORDING_SERIES:
            title = t('RECORDINGS.SERIES_RECORDINGS');
            break;
        default:
            break;
    }

    return title;
};

export const DAY_NAMES = {
    0: 'DATE.SUNDAY',
    1: 'DATE.MONDAY',
    2: 'DATE.TUESDAY',
    3: 'DATE.WEDNESDAY',
    4: 'DATE.THURSDAY',
    5: 'DATE.FRIDAY',
    6: 'DATE.SATURDAY'
};

export const getDayTranslationShort = (day) => t(DAY_NAMES[day]).substring(0, 2) || '';

export const getParentalRating = (itemData) => {
    const { parentalRating } = itemData;
    if (!parentalRating) return null;

    if (typeof parentalRating === 'string') {
        const match = parentalRating.match(/\d+/g);
        return match ? Number(match[0]) : null;
    }

    return !Number.isNaN(parentalRating) ? parentalRating : Object.values(parentalRating)[1];
};

export const prependZero = (number) => (String(number).length > 1 ? number : `0${number}`);

export const HIT_SLOP_FULL_SCREEN = {
    top: 2000,
    right: 2000,
    bottom: 2000,
    left: 2000
};

export const ratingToStars = (rating, maxStars = 5) => Math.round((rating / 100) * maxStars);

export const getDurationString = (durationSeconds, withoutTimeRemainingText) => {
    if (!durationSeconds) return null;
    const minString = t('COMMON.MIN');
    const timeRemainingLeft = t('COMMON.REMAINING_LEFT');
    const timeRemainingRight = t('COMMON.REMAINING_RIGHT');
    const totalMins = Math.round(durationSeconds / 60);
    const hrs = Math.floor(totalMins / 60);
    const mins = totalMins % 60;

    if (withoutTimeRemainingText) {
        if (!hrs) return `${mins || 1} ${t(minString)}`;
        return `${hrs} ${t('COMMON.H')} ${mins} ${minString}`;
    }
    // Display one minute if time is below minute
    if (!hrs) return `${timeRemainingLeft} ${mins || 1} ${t(minString)} ${timeRemainingRight}`;
    return `${timeRemainingLeft} ${hrs} ${t(
        'COMMON.H'
    )} ${mins} ${minString} ${timeRemainingRight}`;
};

export const getDurationMinutesString = (durationSeconds, minString = 'COMMON.MIN') => {
    if (!durationSeconds) return '';
    const totalMins = Math.round(durationSeconds / 60);
    return `${totalMins} ${t(minString)}`;
};

export const TitleLengthEnum = Object.freeze({
    SHORT_DATE: 'short-date',
    SHORT_EPISODE: 'short-episode',
    SHORT_SHOW: 'short-show',
    LONG: 'long'
});

export const getShortDateTitle = (item) =>
    toLocaleDateString(item.getStartTime(), item.locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
    });

export const getShortEpisodeTitle = (item) => {
    const episodeTitle =
        item.data?.episodeTitle ||
        item.data?.schedule?.program?.programValue?.episodeTitle ||
        item.data?.name;
    const episodeId = item.data?.episodeId || item.data?.schedule?.program?.programValue?.episodeId;
    const dateString = toLocaleDateString(item.getStartTime(), item.locale);
    if (
        (item.modelType === ITEM_TYPES.RECORDING || item.modelType === ITEM_TYPES.TV) &&
        episodeId
    ) {
        if (!episodeTitle) return `${t('COMMON.EPISODE_LONG')} ${episodeId} - ${dateString}`;
        return `${episodeId}. ${episodeTitle} - ${dateString}`;
    }
    if (!episodeId) return dateString;
    if (!episodeTitle) return `${t('COMMON.EPISODE_LONG')} ${episodeId}`;
    return `${episodeId}. ${episodeTitle}`;
};

export const getShortShowTitle = (item) => {
    const showTitle = item.data?.name || item.data?.title;
    const episodeId = item.data?.episodeId;
    if (!episodeId) return `${showTitle}`;
    return `${showTitle} (${episodeId})`;
};

export const getLongTitle = (item) => {
    const episodeTitle = item.data?.episodeTitle;
    const episodeId = item.data?.episodeId;
    const name = item.data?.name;
    if (!episodeId) return `${name}`;
    if (!episodeTitle) return getShortShowTitle(item);
    return `${name} (${episodeId}): ${episodeTitle}`;
};

export const getTvTitle = (item, length) => {
    switch (length) {
        case TitleLengthEnum.LONG:
            return getLongTitle(item);
        case TitleLengthEnum.SHORT_EPISODE:
            return getShortEpisodeTitle(item);
        case TitleLengthEnum.SHORT_DATE:
            return getShortDateTitle(item);
        // TODO: Fix after doc review
        // eslint-disable-next-line default-case-last
        default:
        case TitleLengthEnum.SHORT_SHOW:
            return getShortShowTitle(item);
    }
};

export const getAssetTitle = (item, format = TITLE_NUMBERING) =>
    item.getTitle(format, { Episode: t('TV.EPISODE') });

export const getAssetDescription = (item, lang) => {
    if (!item.modelType) {
        return item.descriptions
            ? item.descriptions.find((desc) => desc.locale === lang)?.value
            : '';
    }
    if (item.modelType === ITEM_TYPES.TV) {
        return item.data.longDescription;
    }
    if (item.modelType === ITEM_TYPES.SERIES) {
        return item.data.descriptions
            ? item.data.descriptions.find((desc) => desc.locale === lang)?.value
            : '';
    }
    if (item.modelType === ITEM_TYPES.SEASON) {
        return item.season.descriptions
            ? item.season.descriptions.find((desc) => desc.locale === lang)?.value
            : '';
    }
    const localizedDescription = item.data.descriptions
        ? item.data.descriptions.find((desc) => desc.locale === lang)?.value
        : '';
    return localizedDescription || item.getSubtitle() || '';
};

export const getAssetImageUrl = (item, key, resolution = 'HIGH', deviceType = 'MOBILE') => {
    try {
        return item.getImageUrl(key, resolution, deviceType) || '';
    } catch {
        return '';
    }
};

export const getSearchVODAssetImageUrl = (
    item,
    key,
    resolution = 'HIGH',
    deviceType = 'MOBILE'
) => {
    try {
        if (item.data.data && item.modelType !== ITEM_TYPES.RECORDING_SERIES) {
            return item.data.getImageUrl(key, resolution, deviceType);
        }
        return item.getImageUrl(key, resolution, deviceType) || '';
    } catch {
        return '';
    }
};

export const getImageKeyByItemType = (item) => {
    if (isSeries(item) || isSeason(item) || isEpisode(item) || isVod(item)) return 'POSTER';
    return 'PREVIEW';
};

const getItemProductionAndRelease = (item) => {
    const { data } = item;
    return `${data?.productionCountries ? `${data.productionCountries}, ` : ''} ${
        data?.releaseYear || ''
    }`;
};

const getBaseSubtitle = (item, isItemVod = false) => {
    const { data } = item;
    const duration = getDurationMinutesString(
        data.duration,
        isItemVod ? 'COMMON.MIN' : 'COMMON.MINUTES_REMAINING'
    );
    const actualDuration = duration ? `| ${duration}` : '';
    const productionCountries = data?.productionCountries ? `${data.productionCountries}, ` : '';
    let subtitle = `${productionCountries} ${data.releaseYear || ''} ${actualDuration}`;
    // TODO(mCOre): Parental rating for VOD, series and episode is another object
    const parentalRating = item.getMetaData().getParentalRatingString();
    if (parentalRating && !Number.isNaN(parentalRating)) {
        subtitle += ` | ${parentalRating}`;
    }

    // sometimes there was whitespace string and it just ruined everything
    subtitle = subtitle.trim();
    return subtitle;
};

const getLiveTvSubtitle = (item) => {
    const { data: rawData } = item;
    const data = rawData.data || rawData;
    const date = new Date(data.start);
    const dayText = formatDay(date.getDay(), 2);
    const day = prependZero(date.getDate());
    const month = prependZero(date.getMonth() + 1);

    let subtitle = `${dayText}., ${day}.${month}. | ${getItemStartTime(data)} - ${getItemEndTime(
        data
    )}`;

    if (Device.isTabletOrWeb) {
        subtitle += ` | ${data.channelname || data.channelName}`;
    } else {
        subtitle += `\n${data.channelname || data.channelName}`;
    }

    const parentalRating = getParentalRating(data);
    if (parentalRating && !Number.isNaN(parentalRating)) {
        subtitle += ` | ${parentalRating}+`;
    }

    return subtitle;
};

const getSeriesSeasonSubtitle = (releaseYear = '', countString = '', parentalRating) => {
    let subtitle = '';
    if (releaseYear) subtitle += releaseYear;
    if (countString) subtitle += `| ${countString} `;

    if (parentalRating && !Number.isNaN(parentalRating)) {
        subtitle += `${subtitle.length > 0 ? '| ' : ''}${parentalRating}+`;
    }

    return subtitle;
};

const getSeriesSubtitle = (item) => {
    const seriesData = item.data;
    const { seasons } = seriesData;
    const seasonsString = getGrammarCountString(seasons?.length, seriesGrammarTranslationKeys);
    const parentalRating = getParentalRating(seriesData);
    return getSeriesSeasonSubtitle(seriesData.releaseYear, seasonsString, parentalRating);
};

const getSeasonSubtitle = (item) => {
    const episodesString = getGrammarCountString(
        item.episodes?.length,
        episodesGrammarTranslationKeys
    );
    const parentalRating = getParentalRating(item.series);
    return getSeriesSeasonSubtitle(item.season.releaseYear, episodesString, parentalRating);
};

const getTvSeriesSubtitle = (item, isDetail) => {
    if (!isDetail) {
        const detail = item?.getMetaData()?.metaData?.detail;
        const startTime = 'startTime' in detail ? detail.startTime : detail.start;
        const date = new Date(startTime);
        const subtitle = `${getDayTranslationShort(date.getDay())}., ${addLeadingZero(
            date.getDate()
        )}.${addLeadingZero(date.getMonth() + 1)}. | ${addLeadingZero(
            date.getHours()
        )}:${addLeadingZero(date.getMinutes())}`;
        return `${subtitle}`;
    }

    const countEpisodes = item.getEpisodes().length;
    const NumberOfEpisodesString = getGrammarCountString(
        countEpisodes,
        episodesGrammarTranslationKeys
    );
    const parentalRating = getParentalRating(item.data);
    if (parentalRating)
        return `${item?.data.channelName} | ${NumberOfEpisodesString} | ${parentalRating}`;
    return `${item?.data.channelName} | ${NumberOfEpisodesString}`;
};
const getRecordingsSubtitle = (item) => {
    let subtitle = item.data?.channelName || item.data?.channelname || item.data.data.channelName;
    const date = new Date(item.getStartTime());
    const dayDate = `${getDayTranslationShort(date.getDay())}., ${addLeadingZero(
        date.getDate()
    )}.${addLeadingZero(date.getUTCMonth() + 1)}.`;
    const airTime = `${formatTime(item.getStartTime())} - ${formatTime(item.getEndTime())}`;
    subtitle = `${dayDate} | ${airTime}\n${subtitle}`;

    return `${subtitle}`;
};

export const getAssetSubtitle = (item, isDetail = false) => {
    switch (item.modelType) {
        case ITEM_TYPES.VOD:
            return getBaseSubtitle(item, true);
        case ITEM_TYPES.TV:
            return getLiveTvSubtitle(item);
        case ITEM_TYPES.EPISODE:
            return getBaseSubtitle(item, true);
        case ITEM_TYPES.SEASON:
            return getSeasonSubtitle(item);
        case ITEM_TYPES.SERIES:
            return getSeriesSubtitle(item);
        case ITEM_TYPES.TV_SERIES:
        case ITEM_TYPES.RECORDING_SERIES:
            return getTvSeriesSubtitle(item, isDetail);
        case ITEM_TYPES.RECORDING:
            return getRecordingsSubtitle(item);
        default:
            return getBaseSubtitle(item);
    }
};

export const getAssetGenre = (item, lang) => {
    let genres = '';
    try {
        if (!item.getMetaData().detail?.categories) return genres;
        genres = item
            .getMetaData()
            .detail.categories[0].genreList.map(
                (genreListItem) =>
                    genreListItem.genreTransList.find((tran) => tran.locale === lang)?.name
            )
            .filter((genre) => Boolean(genre))
            .join(', ');
    } catch (err) {
        console.log(err);
    }
    return genres;
};

export const getRemainingSeconds = (item) => {
    const totalTime = item.getEndTime() - getCurrentTime();

    return Math.ceil(totalTime / 1000);
};

// eslint-disable-next-line max-statements
const getInfoListItemsBase = (item, lang) => {
    const data = isSeason(item) ? item.season : item.data || {};
    const items = [];

    const metadata = item?.getMetaData()?.detail || {};

    if (data.titleOriginal) {
        items.push({
            label: t('COMMON.TITLE_ORIGINAL'),
            values: [
                {
                    value: data.titleOriginal
                }
            ]
        });
    }

    const genres = getAssetGenre(item, lang);
    if (genres) {
        items.push({
            label: t('COMMON.GENRE'),
            values: [
                {
                    value: genres
                }
            ]
        });
    }

    const durationString = getDurationMinutesString(item.data?.duration);
    const productionAndRelease = getItemProductionAndRelease(item);
    if (productionAndRelease.trim()) {
        items.push({
            label: t('COMMON.INFO'),
            values: [
                {
                    value: `${productionAndRelease}${durationString && ` | ${durationString}`}`
                }
            ]
        });
    }

    const audioDubs = metadata.dubbedLanguage?.join(', ');
    if (audioDubs) {
        items.push({
            label: t('COMMON.AUDIO'),
            values: [
                {
                    value: audioDubs.toUpperCase()
                }
            ]
        });
    }

    const subs = metadata.subtitles?.join(', ');
    if (subs) {
        items.push({
            label: t('COMMON.SUBTITLES'),
            values: [
                {
                    value: subs
                }
            ]
        });
    }

    if (metadata?.director?.commaDelimitedFullNames) {
        const values = data.director?.commaDelimitedFullNames.split(',');
        items.push({
            label: t('COMMON.DIRECTOR'),
            values: values.map((value) => ({
                value,
                link: {
                    route: 'Search',
                    params: {
                        queryScope: 'CAST'
                    }
                }
            }))
        });
    }

    if (metadata?.actors?.commaDelimitedFullNames) {
        const values = data.actors?.commaDelimitedFullNames.split(',');
        items.push({
            label: t('COMMON.ACTORS'),
            values: values.map((value) => ({
                value,
                link: {
                    route: 'Search',
                    params: {
                        queryScope: 'CAST'
                    }
                }
            }))
        });
    }

    if (data.licenceTo) {
        const formattedLicenceTo = toLocaleDateString(data.licenceTo, lang, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
        items.push({
            label: t('COMMON.LICENCE'),
            values: [
                {
                    value: `${t('COMMON.AVAILABLE_FOR_RENT_UNTIL')}: ${formattedLicenceTo}`
                }
            ]
        });
    }

    return items;
};

const getInfoListItemsTv = (item) => {
    const { data } = item;
    const items = [];

    const metadata = item?.getMetaData().detail || {};

    if (data.titleOriginal) {
        items.push({
            label: t('COMMON.TITLE_ORIGINAL'),
            values: [
                {
                    value: data.titleOriginal
                }
            ]
        });
    }

    const duration = (item.getEndTime() - item.getStartTime()) / 1000;
    const durationString = getDurationMinutesString(duration);
    const category = item.getCategory();
    const releaseYear = item.metaData.getReleaseYearString();
    const releaseYearString = releaseYear ? `${releaseYear} | ` : '';
    if (durationString && category) {
        items.push({
            label: t('COMMON.INFO'),
            values: [
                {
                    value: `${category} | ${releaseYearString}${durationString}`
                }
            ]
        });
    }

    if (metadata.directors && metadata.directors.length > 0) {
        const values = metadata.directors;
        items.push({
            label: t('COMMON.DIRECTOR'),
            values: values.map((value) => ({
                value,
                link: {
                    route: 'Search',
                    params: {
                        queryScope: 'CAST'
                    }
                }
            }))
        });
    }

    if (metadata.actors && metadata.actors.length > 0) {
        const values = metadata.actors;
        items.push({
            label: t('COMMON.ACTORS'),
            values: values.map((value) => ({
                value,
                link: {
                    route: 'Search',
                    params: {
                        queryScope: 'CAST'
                    }
                }
            }))
        });
    }

    return items;
};

export const getInfoListItems = (item, lang) => {
    if (isTvLikeType(item)) {
        return getInfoListItemsTv(item, lang);
    }

    if (isRecordingSeries(item)) {
        const detail = v2ScheduleToV1ProgramDetail(item.data.schedule);
        return getInfoListItemsTv(new ProgramModel(detail), lang);
    }

    return getInfoListItemsBase(item, lang);
};

export const getTvStreamOptions = async (item, service = STREAM_TYPE.LIVE) => {
    const options = {
        service,
        name: await getDeviceName(),
        devtype: getMcoreDeviceType()
    };
    // If recording is still being recorded, treat it as live
    if (
        service === STREAM_TYPE.LIVE ||
        (isRecording(item) && !itemHasEnded(item)) ||
        item.forceUsingChannelId
    ) {
        options.id = item.getChannelId();
    } else if (service === STREAM_TYPE.ARCHIVE) {
        options.id = item.getId();
    }

    return options;
};

export const getBaseStreamOptions = (item, service) => ({
    id: item.getId(),
    service
});

export const getSeriesStreamOptions = (item, service) => {
    const firstEpisode = getFirstEpisode(item?.seasons[0]?.episodes[0]);
    return {
        id: firstEpisode.data.id,
        service
    };
};

export const getSeasonStreamOptions = (item, service) => {
    const firstEpisode = getFirstEpisode(item?.episodes[0]);
    return {
        id: firstEpisode.data.id,
        service
    };
};

export const getRecordingStreamOptions = (item) => {
    const { data } = getRecordingForItem(item);
    return {
        id: data.id,
        service: STREAM_TYPE.DVR
    };
};

export const isStreamLive = ({ streamType }) => streamType === STREAM_TYPE.LIVE;
export const isStreamTimeshift = ({ streamType }) => streamType === STREAM_TYPE.TIMESHIFT;
export const isStreamArchive = ({ streamType }) => streamType === STREAM_TYPE.ARCHIVE;
export const isStreamVod = ({ streamType }) => streamType === STREAM_TYPE.VOD;
export const isStreamDvr = ({ streamType }) => streamType === STREAM_TYPE.DVR;

export const getStreamOptionsByType = async (item, service, profile) => {
    let options;

    if (item.modelType === ITEM_TYPES.TV) {
        options = await getTvStreamOptions(item, service);
    } else if (item.modelType === ITEM_TYPES.SERIES) {
        options = getSeriesStreamOptions(item, service);
    } else if (item.modelType === ITEM_TYPES.SEASON) {
        options = getSeasonStreamOptions(item, service);
    } else if (item.modelType === ITEM_TYPES.RECORDING) {
        // If recording is still being recorded, treat it as live
        if (itemHasEnded(item)) {
            options = getRecordingStreamOptions(item);
        } else {
            options = await getTvStreamOptions(item, STREAM_TYPE.LIVE);
        }
    } else {
        options = getBaseStreamOptions(item, service);
    }

    if (profile) {
        options.prof = profile;
    }

    options.ecid = '';
    options.drm = 'verimatrix';

    return options;
};

export const getErrorMessage = (error) => {
    if (error.errorCode === 'NO_NETWORK') return t('COMMON.NETWORK_DISCONNECTED_MSG');
    if (error.errorCode === 'NO_ACTIVE_ACCOUNTS') return 'NO_ACTIVE_ACCOUNTS';
    return replaceBrWithNewline(
        error.localizedMessage ||
            error.errorMessage ||
            error.errorCode ||
            error.message ||
            error.errorString ||
            t('ERROR.UNKNOWN')
    );
};

export const getProgressPercentage = (item) => {
    const start = item.getStartTime();
    const end = item.getEndTime();
    const now = getCurrentTime();
    const percent = Math.round(((now - start) / (end - start)) * 100);

    return Math.min(Math.max(percent, 0), 100);
};

export const getItemSubtitle = (item) => {
    let subtitle = '';
    if (itemHasStarted(item) && !itemHasEnded(item)) {
        const duration = getRemainingSeconds(item);
        subtitle = getDurationString(duration);
    }

    return subtitle;
};

export const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';

    const exp = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const sizeUnit = Math.floor(Math.log(bytes) / Math.log(exp));

    return `${parseFloat((bytes / exp ** sizeUnit).toFixed(dm))} ${sizes[sizeUnit]}`;
};

const DEVICE_TYPE_ICONS = {
    STB: 'device_stb',
    SMART_TV: 'tv',
    MOBILE_TABLET: 'mobile_tablet_devices',
    WEB_BROWSER: 'laptop',
    OTHER: 'devices'
};

export const getIconByDeviceType = (deviceType) =>
    DEVICE_TYPE_ICONS[deviceType] || DEVICE_TYPE_ICONS.OTHER;

export const areChannelsFavorites = (channels, favorites) => {
    if (!channels?.length && !favorites?.length) return false;
    if (channels.length !== favorites.length) return false;
    if (channels[0].channel?.channelId !== favorites[0]?.channelId) return false;
    if (
        channels[channels.length - 1].channel?.channelId !==
        favorites[favorites.length - 1]?.channelId
    )
        return false;
    return true;
};
